import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, ArrowLeft } from "lucide-react";

const NotFound: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 relative">
      {/* Background Pattern */}
      <div
        className="absolute inset-0 bg-repeat pointer-events-none"
        style={{
          backgroundImage: "url('/grid_white.png')",
          backgroundSize: "80px auto",
          opacity: 0.2,
        }}
      />

      {/* Studio1 Logo */}
      <Link to="/" className="mb-8 relative z-10">
        <img
          src="/logo.png"
          alt="Studio1"
          className="h-12 hover:opacity-80 transition-opacity"
        />
      </Link>

      <div className="max-w-md w-full text-center space-y-6 relative z-10">
        {/* 404 Badge */}
        <div className="flex justify-center">
          <div className="bg-gradient-to-r from-semantic-accent to-semantic-secondary text-white text-6xl font-bold px-8 py-4 rounded-2xl shadow-lg">
            404
          </div>
        </div>

        {/* Error Message */}
        <div className="space-y-3">
          <h1 className="text-3xl font-bold text-foreground">Page Not Found</h1>
          <p className="text-muted-foreground">
            The page you&apos;re looking for doesn&apos;t exist or has been
            moved.
          </p>
        </div>

        {/* Requested Path */}
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-sm text-muted-foreground">
            <span className="font-medium">Requested path:</span>{" "}
            <code className="bg-muted px-2 py-1 rounded-md text-foreground font-mono text-xs">
              {location.pathname}
            </code>
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          <Button
            onClick={handleGoBack}
            variant="grey"
            size="lg"
            className="gap-2 rounded-[8px]"
          >
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </Button>
          <Button
            asChild
            variant="pink"
            size="lg"
            className="gap-2 rounded-[8px]"
          >
            <Link to="/">
              <Home className="h-4 w-4" />
              Home
            </Link>
          </Button>
        </div>

        {/* Quick Links */}
        <div className="pt-6 border-t border-border">
          <p className="text-sm text-muted-foreground mb-3">
            Or try one of these:
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button asChild variant="grey" className="rounded-[8px] h-8 px-4">
              <Link to="/workflows">Workflows</Link>
            </Button>
            <Button asChild variant="grey" className="rounded-[8px] h-8 px-4">
              <Link to="/productions">Studio</Link>
            </Button>
            <Button asChild variant="grey" className="rounded-[8px] h-8 px-4">
              <Link to="/auth">Sign In</Link>
            </Button>
          </div>
        </div>

        {/* Help Text */}
        <div className="pt-6 text-xs text-muted-foreground">
          <p>
            If you believe this is an error, please{" "}
            <a
              href="mailto:support@aims.team"
              className="text-primary hover:underline"
            >
              contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
